import * as React from 'react';
import * as Redux from 'redux';
import { connect } from 'react-redux';

import { WizardPage, WizardPageEnum } from './interfaces/wizard-page';

import Modal from '../../misc/components/modal';
import { FileSelectionPage } from './components/FileSelectionPage';
import { ConfirmationPage } from './components/ConfirmationPage';
import { ImportingPage } from './components/ImportingPage';
import { ImportSummaryPage } from './components/ImportSummaryPage';
import { StudyCoughEventType } from '../../misc/api/apiTypes';
import * as Actions from './ImportEventsActions';

import { ImportEvent } from './ImportEventsReducer';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import { MainState } from '../../Core/reducers';
import { CoughAnalysisSet } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';

interface ImportEventsWizardOwnProps {
    analysisSet: CoughAnalysisSet;
    onClose: () => void;
}

interface ImportEventsWizardProps extends ImportEventsWizardOwnProps {
    currentPage: WizardPageEnum;
    eventsFileName: string;
    nextAction: any;
    backAction: any;
    eventsList: Array<ImportEvent>;
    isErrorVisible: boolean;

    dispatch?: Redux.Dispatch<MainState>;
}

declare const rootUrl;

export const ImportEventsWizard = (props: ImportEventsWizardProps) => {
    const [error, setError] = React.useState<string>();
    const [studyCoughEventType, setStudyCoughEventType] = React.useState<StudyCoughEventType>();
    const [hourlySegment, setHourlySegment] = React.useState<number | string>();
    const { study } = useCurrentStudyConfigurationContext();
    const webPortalClient = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        legacyClient.getStudyCoughEventTypes({
            Filters: [{
                Member: 'StudyId',
                Values: [study.id],
            }, {
                Member: 'EventName',
                Values: ['Cough Peak'],
            }]
        })
            .then(json => {
                setStudyCoughEventType(json.Data[0]);
            }); 
    }, []);

    React.useEffect(() => {
        detectFinishedImporting(props);
    }, [props.eventsList]);

    const detectFinishedImporting = (nextProps: ImportEventsWizardProps) => {
        const unProcessedEventsCount = nextProps.eventsList.filter(x => x.IsImporting <= 1).length;

        if (props.currentPage === WizardPageEnum.Importing && unProcessedEventsCount === 0) {
            props.dispatch(Actions.JumpToImportSummaryAction.create());
        }
    }

    const handleClickBack = () => {
        if (props.backAction === Actions.CancelAction) {
            unmount();            
        } else {
            props.dispatch(props.backAction.create());
        }
    }

    const handleClickNext = () => {
        if (props.nextAction === Actions.JumpToFinishAction) {
            unmount();
            return;
        }
        validateState(props.nextAction);
    }
    
    const handleErrorMessage = (message: string) => {
        if (message === null) {
            setError(undefined);
        } else {
            setError(message);
        }
    }

    const handleSetHourlySegment = (selectedHourlySegment: number | string) => {
        handleErrorMessage(null);
        setHourlySegment(selectedHourlySegment);
    }

    const isANumber = (val: string | number): val is number => {
        return !isNaN(val as number);
    }

    const importEvents = () => {
        const timeQuantity = 60;

        let secondsOffset = 0;
        if (isANumber(hourlySegment))
            secondsOffset = (hourlySegment - 1) * timeQuantity * timeQuantity;

        props.dispatch(Actions.ImportEventAsyncAction.create(props.eventsList, props.analysisSet.id, secondsOffset, studyCoughEventType.Id, webPortalClient.client));
    }

    const validateState = (nextAction) => {
        if (!props.eventsFileName) {
            return handleErrorMessage("No File Selected.");
        } else if (!hourlySegment) {
            return handleErrorMessage("No Hourly Segment Selected.");
        } else if (!props.eventsList || !props.eventsList.length) {
            return handleErrorMessage("There are no events in the selected file");
        }

        props.dispatch(nextAction.create());
    }

    const unmount = () => {
        props.dispatch(Actions.CloseImportEventsDialogAction.create());
        props.onClose();
    }

    const page = (): WizardPage => {
        switch (props.currentPage) {
            case WizardPageEnum.FileSelection:
                return {
                    component: <FileSelectionPage handleSetHourlySegment={(hs) => handleSetHourlySegment(hs)}
                        handleSetEventsFile={(name) => props.dispatch(Actions.SetEventsFileAction.create(name))}
                        handleEventsDataChange={(list) => props.dispatch(Actions.SetEventsListAction.create(list))}
                        handleErrorMessage={(message) => handleErrorMessage(message)}
                        eventsFileSelected={props.eventsFileName}
                        hourlySegmentSelected={hourlySegment}
                        analysisSet={props.analysisSet} />
                };
            case WizardPageEnum.Confirmation:
                return {
                    component: <ConfirmationPage analysisSet={props.analysisSet} hourSegment={hourlySegment} eventCount={props.eventsList.length} />
                };
            case WizardPageEnum.Importing:
                return {
                    component: <ImportingPage startImporting={() => importEvents()} eventsList={props.eventsList} />
                };
            case WizardPageEnum.ImportSummary:
                return {
                    component: <ImportSummaryPage eventsList={props.eventsList} />
                }
            case WizardPageEnum.Cancel: unmount(); break;
            case WizardPageEnum.Finish: unmount(); break;
        }
    }

    const currentPage = page();

    return (
        <Modal header="Import Cough Events" show={true}>
            <div className="modal-body">
                <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                    {error}
                </Alert>
                {currentPage.component}
            </div>
            <div className="modal-footer">
                <button
                    className="btn btn-primary"
                    disabled={props.currentPage === WizardPageEnum.Importing || hourlySegment === undefined}
                    onClick={handleClickNext}>{props.nextAction === Actions.JumpToFinishAction ? 'Finish' : 'Next'}</button>
                            
                {props.currentPage != WizardPageEnum.ImportSummary && <button
                    className="btn btn-primary btn-outline"
                    disabled={props.currentPage === WizardPageEnum.Importing}
                    onClick={handleClickBack}>{props.backAction === Actions.CancelAction ? 'Cancel' : 'Back'}</button>}
            </div>
        </Modal>
    );
}

function mapStateToProps(state: MainState, ownProps: ImportEventsWizardOwnProps): ImportEventsWizardProps {
    return {
        currentPage: state.importEvents.currentPage,
        eventsFileName: state.importEvents.eventsFileName,
        analysisSet: ownProps.analysisSet,
        onClose: ownProps.onClose,
        nextAction: state.importEvents.nextAction,
        backAction: state.importEvents.backAction,
        eventsList: state.importEvents.eventsList,
        isErrorVisible: state.importEvents.isErrorVisible
    };
}

export default connect<ImportEventsWizardProps, any, ImportEventsWizardOwnProps>(mapStateToProps)(ImportEventsWizard);
